import { NavLink } from "react-router-dom";
import { LayoutDashboard, Sparkles, LineChart, Telescope, Plug, LogOut, Users, Zap, Rocket, Dna, Mail, BookOpen, Layers, History, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ProductTour from "./ProductTour";

const ALL_LINKS = [
  { to: "/dashboard", label: "Performance", icon: LayoutDashboard, id: "nav-performance", roles: ["admin", "buyer", "viewer"] },
  { to: "/reports", label: "Reports", icon: Layers, id: "nav-reports", roles: ["admin", "buyer", "viewer"] },
  { to: "/action-history", label: "Action History", icon: History, id: "nav-history", roles: ["admin", "buyer", "viewer"] },
  { to: "/campaigns/new", label: "Campaign Builder", icon: Sparkles, id: "nav-builder", roles: ["admin", "buyer"] },
  { to: "/insights", label: "AI Insights", icon: LineChart, id: "nav-insights", roles: ["admin", "buyer", "viewer"] },
  { to: "/rules", label: "Auto-Rules", icon: Zap, id: "nav-rules", roles: ["admin", "buyer"] },
  { to: "/optimize", label: "Optimize", icon: Rocket, id: "nav-optimize", roles: ["admin", "buyer"] },
  { to: "/intelligence", label: "Intelligence", icon: Dna, id: "nav-intelligence", roles: ["admin", "buyer"] },
  { to: "/scouting", label: "Scouting", icon: Telescope, id: "nav-scouting", roles: ["admin", "buyer"] },
  { to: "/digest", label: "Daily Digest", icon: Mail, id: "nav-digest", roles: ["admin", "buyer", "viewer"] },
  { to: "/connections", label: "My Facebook", icon: Plug, id: "nav-connections", roles: ["admin", "buyer"] },
  { to: "/team", label: "Team & Keys", icon: Users, id: "nav-team", roles: ["admin"] },
  { to: "/admin/logs", label: "Admin Logs", icon: AlertCircle, id: "nav-admin-logs", roles: ["admin"] },
  { to: "/docs", label: "Docs", icon: BookOpen, id: "nav-docs", roles: ["admin", "buyer", "viewer"] },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const role = user?.role || "admin";
  const links = ALL_LINKS.filter((l) => l.roles.includes(role));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <aside className="w-[240px] fixed h-screen border-r border-slate-200 bg-white z-20 flex flex-col">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <span className="text-[10px] font-mono font-bold text-black">S1</span>
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>RSOC · Hub</div>
              <div className="text-[10px] text-slate-500 font-mono">v0.1 preview</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.7} />
                <span>{l.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-slate-200 space-y-2">
          <div className="px-2 py-1.5 flex items-center gap-2 text-xs">
            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] font-mono text-slate-500">
                  {(user?.name || "?").slice(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate text-xs text-slate-900" data-testid="user-name">{user?.name}</div>
              <div className="truncate text-[10px] text-slate-500 font-mono">{user?.email} · {role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} /> Log out
          </button>
        </div>
      </aside>
      <main className="ml-[240px]">{children}</main>
      <ProductTour />
    </div>
  );
}
